import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { markNotificationRead } from '../../services/notifications';
import { Notification } from '../../types/notification';
import { parseApiDate } from '../../utils/apiDates';
import {
  BookOpenIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  BellIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface NotificationItemProps {
  notification: Notification;
  onRead: () => void;
  /** Mark this notification read in place, without opening/navigating. */
  onMarkRead?: (id: string) => void;
  onDelete?: (id: string) => void;
  isDeleting?: boolean;
  isEditMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  'data-testid'?: string;
}

/**
 * NotificationItem Component
 *
 * Single notification card with icon, title, message, and timestamp.
 *
 * Features:
 * - Type-specific icons
 * - Read/unread styling
 * - Relative timestamps
 * - Click to mark as read
 * - Hover effects
 *
 * @example
 * <NotificationItem notification={notification} onRead={handleRead} />
 */
const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onRead,
  onMarkRead,
  onDelete,
  isDeleting = false,
  isEditMode = false,
  isSelected = false,
  onToggleSelect,
  'data-testid': testId = 'notification-item',
}) => {
  const getIcon = (type: string) => {
    const iconClass = 'w-5 h-5';

    switch (type) {
      case 'course_assigned':
        return <BookOpenIcon className={iconClass} />;
      case 'join_request':
        return <CheckCircleIcon className={iconClass} />;
      case 'chat_message':
        return <ChatBubbleLeftRightIcon className={iconClass} />;
      case 'calendar_event':
        return <CalendarIcon className={iconClass} />;
      case 'system':
        return <BellIcon className={iconClass} />;
      default:
        return <BellIcon className={iconClass} />;
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'course_assigned':
        return 'text-primary-500';
      case 'join_request':
        return 'text-success-600';
      case 'chat_message':
        return 'text-primary-500';
      case 'calendar_event':
        return 'text-warning-600';
      case 'system':
        return 'text-text-secondary';
      default:
        return 'text-text-secondary';
    }
  };

  const handleClick = async () => {
    if (isEditMode) {
      onToggleSelect?.(notification.id);
      return;
    }
    if (!notification.read) {
      await markNotificationRead(notification.id).catch(() => {});
    }
    onRead();
  };

  const relativeTime = formatDistanceToNow(parseApiDate(notification.created_at), {
    addSuffix: true,
  });

  return (
    <div
      className={`group relative ${isDeleting ? 'opacity-50 pointer-events-none transition-opacity duration-150' : ''}`}
      data-testid="notification-item-wrapper"
    >
      <button
        onClick={handleClick}
        className={`
          w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-background-hover focus:outline-none focus:bg-background-hover transition-colors
          ${!notification.read ? 'bg-primary-50 dark:bg-primary-900/10' : ''}
        `}
        data-testid={testId}
        data-read={notification.read}
      >
        {/* Edit-mode checkbox */}
        {isEditMode && (
          <div
            className="flex-shrink-0 flex items-center self-center"
            data-testid={`${testId}-checkbox-wrapper`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect?.(notification.id)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-border-primary text-primary-500 cursor-pointer"
              data-testid={`${testId}-checkbox`}
              aria-label={`Select notification: ${notification.title}`}
            />
          </div>
        )}

        {/* Icon */}
        <div
          className={`flex-shrink-0 mt-0.5 ${getIconColor(notification.type)}
            ${isEditMode ? 'transition-[padding] duration-150 pl-0' : ''}`}
          data-testid={`${testId}-icon`}
        >
          {getIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="flex items-start gap-2">
            {!notification.read && (
              <span
                className="flex-shrink-0 w-2 h-2 mt-1.5 bg-primary-500 rounded-full"
                data-testid={`${testId}-unread-dot`}
              />
            )}
            <p
              className={`text-sm ${
                notification.read ? 'font-normal text-text-secondary' : 'font-semibold text-text-primary'
              }`}
              data-testid={`${testId}-title`}
            >
              {notification.title}
            </p>
          </div>

          {/* Message */}
          <p
            className="text-sm text-text-secondary mt-1 line-clamp-2"
            data-testid={`${testId}-message`}
          >
            {notification.message}
          </p>

          {/* Timestamp */}
          <p
            className="text-xs text-text-tertiary mt-1"
            data-testid={`${testId}-timestamp`}
          >
            {relativeTime}
          </p>
        </div>
      </button>

      {/* Mark-as-read — always visible on unread items so a single
          notification can be cleared without opening it or entering Edit */}
      {onMarkRead && !isEditMode && !notification.read && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMarkRead(notification.id);
          }}
          className="absolute top-2 right-9 p-1 rounded text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-500/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-400 transition-colors"
          data-testid={`${testId}-mark-read-button`}
          aria-label={`Mark as read: ${notification.title}`}
          title="Mark as read"
        >
          <CheckCircleIcon className="w-4 h-4" />
        </button>
      )}

      {/* Delete button — visible on hover, hidden in edit mode */}
      {onDelete && !isEditMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
          className={`
            absolute top-2 right-2
            p-1 rounded text-text-tertiary hover:text-error-600 hover:bg-error-50
            dark:hover:bg-error-900/20
            focus:outline-none focus-visible:ring-1 focus-visible:ring-error-400
            transition-opacity duration-[150ms] ease-out
            ${isDeleting
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100'}
          `}
          data-testid={`${testId}-delete-button`}
          aria-label={`Delete notification: ${notification.title}`}
        >
          {isDeleting ? (
            <div
              className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
              data-testid={`${testId}-delete-spinner`}
            />
          ) : (
            <TrashIcon className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
  );
};

export default NotificationItem;
